import React from 'react';
import { Button } from '../onyx/Button';
import { Panel } from '../onyx/Panel';
import { SectionHeader } from '../onyx/SectionHeader';
/**
 * Member-dashboard sample collection status.
 *
 * Rendering depends on ``phlebotomy_provider``:
 * - Clinic providers (e.g. attune): in-person appointment at the clinic
 * - Mobile/home-draw providers: preferences / phlebotomist scheduling copy
 */

export const CLINIC_PROVIDERS = {
  attune: {
    clinicName: 'Attune Health',
  },
};

export const isClinicSampleCollection = (collection) =>
  Boolean(collection?.phlebotomy_provider && CLINIC_PROVIDERS[collection.phlebotomy_provider]);

/** Initiated clinic visits with a booked time should not show the mobile "Start Now" CTA. */
export const isPendingMobileCollection = (collection) =>
  collection?.status === 'initiated' && !isClinicSampleCollection(collection);

export const isActiveSampleCollection = (collection) => {
  if (!collection) return false;
  if (isClinicSampleCollection(collection) && collection.collection_datetime) {
    return !['cancelled', 'completed', 'done'].includes(collection.status);
  }
  return ['info_provided', 'submitted', 'contacted', 'selected', 'confirmed', 'rescheduled'].includes(
    collection.status,
  );
};

const formatAddress = (collection) => {
  if (!collection?.address_1) return null;
  const street = collection.address_2
    ? `${collection.address_1}, ${collection.address_2}`
    : collection.address_1;
  return `${street}, ${collection.city}, ${collection.state} ${collection.zip_code}`;
};

const formatVisitWhen = (collection) => {
  const formatted = collection.collection_datetime_formatted;
  if (formatted?.formatted) return formatted.formatted;
  if (formatted?.date && formatted?.time) {
    return `${formatted.date} at ${formatted.time}${formatted.timezone ? ` ${formatted.timezone}` : ''}`;
  }
  if (!collection.collection_datetime) return null;
  const raw =
    typeof collection.collection_datetime === 'string'
      ? collection.collection_datetime
      : collection.collection_datetime?.iso;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getMobileStatusInfo = (status) => {
  switch (status) {
    case 'info_provided':
      return {
        label: 'Preferences provided',
        message:
          'Your study visit preferences have been successfully provided! We are working on arranging your visit.',
      };
    case 'submitted':
      return {
        label: 'Order placed',
        message:
          'Your study visit order has been placed. A mobile phlebotomist will contact you soon to schedule.',
      };
    case 'contacted':
      return {
        label: 'Contacted',
        message:
          'A mobile phlebotomist has contacted you. Please work with them to confirm your appointment time.',
      };
    case 'confirmed':
      return {
        label: 'Appointment Confirmed',
        message:
          'Your appointment has been confirmed. The mobile phlebotomist will arrive at your scheduled time.',
      };
    case 'selected':
      return {
        label: 'Phlebotomist Selected',
        message: 'A mobile phlebotomist has been selected and will contact you shortly.',
      };
    case 'rescheduled':
      return {
        label: 'Rescheduled',
        message: 'Your appointment has been rescheduled. Please wait for confirmation.',
      };
    default:
      return { label: status, message: '' };
  }
};

const getClinicStatusInfo = (collection) => {
  const clinic = CLINIC_PROVIDERS[collection.phlebotomy_provider];
  const clinicName = clinic?.clinicName || 'the clinic';
  if (collection.status === 'rescheduled') {
    return {
      label: 'Visit Rescheduled',
      message: `Your clinic visit at ${clinicName} has been rescheduled. Please check your confirmation email for the updated time.`,
    };
  }
  return {
    label: 'Visit Scheduled',
    message: `Your in-person study visit at ${clinicName} is confirmed. Please arrive on time and bring a photo ID.`,
  };
};

const ClinicCollectionDetails = ({ collection }) => {
  const statusInfo = getClinicStatusInfo(collection);
  const clinic = CLINIC_PROVIDERS[collection.phlebotomy_provider];
  const when = formatVisitWhen(collection);
  const address = formatAddress(collection);

  return (
    <div>
      {statusInfo.message && <p className="content__body">{statusInfo.message}</p>}
      <div className="onyx-detail-list">
        <div className="onyx-detail-row">
          <span className="onyx-detail-label">Status</span>
          <span className="onyx-detail-value">{statusInfo.label}</span>
        </div>
        {when ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">When</span>
            <span className="onyx-detail-value">{when}</span>
          </div>
        ) : null}
        {(clinic?.clinicName || address) ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Clinic</span>
            <span className="onyx-detail-value">
              {clinic?.clinicName ? <strong>{clinic.clinicName}</strong> : null}
              {clinic?.clinicName && address ? <br /> : null}
              {address}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const MobileCollectionDetails = ({ collection }) => {
  const statusInfo = getMobileStatusInfo(collection.status);
  const contactMethod =
    collection.preferred_contact_method === 'phone'
      ? 'Phone Call'
      : collection.preferred_contact_method === 'text'
        ? 'Text Message'
        : collection.preferred_contact_method
          ? 'Phone or Text'
          : null;
  const address = formatAddress(collection);

  return (
    <div>
      {statusInfo.message && <p className="content__body">{statusInfo.message}</p>}
      <div className="onyx-detail-list">
        <div className="onyx-detail-row">
          <span className="onyx-detail-label">Status</span>
          <span className="onyx-detail-value">{statusInfo.label}</span>
        </div>
        {collection.phone_number ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Phone</span>
            <span className="onyx-detail-value">{collection.phone_number}</span>
          </div>
        ) : null}
        {contactMethod ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Contact Method</span>
            <span className="onyx-detail-value">{contactMethod}</span>
          </div>
        ) : null}
        {address ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Address</span>
            <span className="onyx-detail-value">{address}</span>
          </div>
        ) : null}
        {collection.special_instructions ? (
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">Special Instructions</span>
            <span className="onyx-detail-value">{collection.special_instructions}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Props:
 *   collections - sample collection objects from the member API
 *   onStartPending - () => void  opens the mobile preferences / submission dialog
 */
export const SampleCollectionStatusSection = ({ collections = [], onStartPending }) => {
  const pendingCollections = collections.filter(isPendingMobileCollection);
  const activeCollections = collections.filter(isActiveSampleCollection);

  if (pendingCollections.length > 0) {
    const collection = pendingCollections[0];
    return (
      <Panel className="dashboard-section" tone="success">
        <SectionHeader
          title="Study visit"
          description="Confirm scheduling details so we can arrange your visit."
        />
        <div className="onyx-detail-list">
          <div className="onyx-detail-row">
            <span className="onyx-detail-label">{collection.study_title}</span>
            <span className="onyx-detail-value">
              Please confirm your details and review our guidelines so we can schedule your study visit.
            </span>
            <Button type="button" variant="primary" onClick={onStartPending}>
              Start Now
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  if (activeCollections.length === 0) {
    return null;
  }

  return (
    <Panel className="dashboard-section">
      <SectionHeader title="Study visit" />
      {activeCollections.map((collection) =>
        isClinicSampleCollection(collection) ? (
          <ClinicCollectionDetails key={collection.id} collection={collection} />
        ) : (
          <MobileCollectionDetails key={collection.id} collection={collection} />
        ),
      )}
    </Panel>
  );
};

export default SampleCollectionStatusSection;
